import React from "react";
import { Icon } from "@iconify/react";
import Button from "../../Components/Button";
import { Typography } from "../../Components/Typography";
import { openFileUrl } from "../IdeasReview/utils";
import EditableAttachmentLinks from "./EditableAttachmentLinks";
import {
  collectAllIdeaAttachments,
  formatFileSize,
  getEditableUrlFieldsForStage,
  getFileIcon,
  mergeAttachmentFormValues,
} from "./ideaDetailUiUtils";

const EditableStageUrlFields = ({ fields, formValues, onFieldChange, disabled = false }) => {
  if (!fields.length) return null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Typography as="h4" className="text-base font-bold text-slate-900">
          Stage links
        </Typography>
        <Typography as="p" className="text-sm text-slate-500 mt-1">
          Supporting document and resource links for this stage.
        </Typography>
      </div>
      {fields.map(({ label, urlField }) => (
        <div
          key={urlField}
          className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 flex flex-col gap-2"
        >
          <div className="flex items-center gap-2">
            <Icon icon="lucide:paperclip" className="h-4 w-4 text-violet-600" />
            <label className="text-sm font-semibold text-slate-800">{label}</label>
          </div>
          <input
            type="url"
            value={formValues[urlField] || ""}
            onChange={(e) => onFieldChange(urlField, e.target.value)}
            placeholder="https://..."
            disabled={disabled}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 disabled:bg-slate-100"
          />
        </div>
      ))}
    </div>
  );
};

const AttachmentFileRow = ({ file }) => {
  const fileIcon = getFileIcon(file.type);

  return (
    <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-md">
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${fileIcon.bg} shadow-sm`}
      >
        <Typography as="span" className="text-xs font-bold text-white">
          {fileIcon.text}
        </Typography>
      </div>
      <div className="min-w-0 flex-1">
        <Typography as="p" className="text-sm font-semibold text-slate-900 truncate">
          {file.name}
        </Typography>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          {file.fileTag && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
              {file.fileTag}
            </span>
          )}
          <span>{formatFileSize(file.size)}</span>
          {file.uploadedOn && file.uploadedOn !== "—" && file.uploadedOn !== "-" && (
            <>
              <span className="text-slate-300">·</span>
              <span>{file.uploadedOn}</span>
            </>
          )}
        </div>
      </div>
      <Button
        variant="secondary"
        size="small"
        disabled={!file.url}
        onClick={() => openFileUrl(file.url)}
        className="shrink-0 border-violet-200 text-violet-700 hover:bg-violet-50"
      >
        View
      </Button>
    </div>
  );
};

const AttachmentsList = ({ attachments }) => {
  if (!attachments.length) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 py-12">
        <Icon icon="lucide:folder-open" className="h-10 w-10 text-slate-300 mb-2" />
        <Typography as="p" className="text-sm text-slate-500">
          No attachments uploaded yet.
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {attachments.map((file) => (
        <AttachmentFileRow key={file.id} file={file} />
      ))}
    </div>
  );
};

const AttachmentsHeader = ({ count }) => (
  <div className="flex items-end justify-between gap-4">
    <div>
      <Typography as="h3" className="text-base font-bold text-slate-900">
        Attached files
      </Typography>
      <Typography as="p" className="text-sm text-slate-500 mt-1">
        Videos, documents, and links submitted with this idea.
      </Typography>
    </div>
    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
      {count} file{count === 1 ? "" : "s"}
    </span>
  </div>
);

const IdeaAttachmentsSection = ({
  engagement,
  canEdit = false,
  activeStage = 0,
  formValues = {},
  onFieldChange,
  onSave,
  saving = false,
  hideSaveButton = false,
}) => {
  const attachments = collectAllIdeaAttachments(
    mergeAttachmentFormValues(engagement, formValues)
  );
  const stageUrlFields = getEditableUrlFieldsForStage(activeStage);

  if (canEdit) {
    return (
      <div className="flex flex-col gap-8">
        <EditableAttachmentLinks
          formValues={formValues}
          onFieldChange={onFieldChange}
          onSave={onSave}
          saving={saving}
          hideSaveButton={hideSaveButton}
        />
        <EditableStageUrlFields
          fields={stageUrlFields}
          formValues={formValues}
          onFieldChange={onFieldChange}
          disabled={saving}
        />
        <div className="flex flex-col gap-4 border-t border-slate-200 pt-6">
          <AttachmentsHeader count={attachments.length} />
          <AttachmentsList attachments={attachments} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <AttachmentsHeader count={attachments.length} />
      <AttachmentsList attachments={attachments} />
    </div>
  );
};

export default IdeaAttachmentsSection;
